import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";

/* ===============================
   AUTH HELPERS (ADDED - REQUIRED)
   SAME AS WORKING PAGE
================================ */
const getSessionToken = () =>
  (localStorage.getItem("session_token") || "").trim();

const getAuthHeaders = () => {
  const token = getSessionToken();
  return {
    "Content-Type": "application/json",
    Accept: "application/json",
    "X-Session-Token": token,          // ✅ REQUIRED FOR BACKEND
    Authorization: `Bearer ${token}`,  // optional, backend ignores
  };
};

const AddInvoice = () => {
  const { patient_id } = useParams();
  const [patient, setPatient] = useState(null);
  const [products, setProducts] = useState([]);
  const [suggestedProducts, setSuggestedProducts] = useState([]); // ✅ NEW
  const [comment, setComment] = useState("");

  const [items, setItems] = useState(
    Array.from({ length: 8 }, () => ({
      description: "",
      quantity: 1,
      amount: "",
      product_id: null,
      unit_price: "",
    }))
  );

  const [submitting, setSubmitting] = useState(false);
  const [leftMargin, setLeftMargin] = useState(
    window.innerWidth >= 1024 ? 250 : 0
  );
  const [discount, setDiscount] = useState();
  const [loadingSuggested, setLoadingSuggested] = useState(false);

  const navigate = useNavigate();

  /* =============================
     Layout resize
  ============================== */
  useEffect(() => {
    const handleResize = () =>
      setLeftMargin(window.innerWidth >= 1024 ? 250 : 0);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  /* =============================
     Patient + suggested products
  ============================== */
  useEffect(() => {
    if (patient_id) {
      fetchPatientDetails(patient_id);
      fetchSuggestedProducts(patient_id);
    }
  }, [patient_id]);

  /* =============================
     Fetch all products
  ============================== */
  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchPatientDetails = async (id) => {
    try {
      const response = await fetch(
        `https://newerp.awasthivitality.com/api/patients/${id}/details`,
        { headers: getAuthHeaders() } // ✅ ADDED (SAFE)
      );
      const data = await response.json();
      if (data.success) setPatient(data.data);
    } catch (error) {
      console.error("Error fetching patient:", error);
    }
  };

  /* =============================
     Fetch suggested products ONLY
  ============================== */
  const fetchSuggestedProducts = async (id) => {
    try {
      setLoadingSuggested(true);
      const response = await axios.get(
        `https://newerp.awasthivitality.com/api/patients/${id}/suggested-products`,
        { headers: getAuthHeaders() } // ✅ ADDED (SAFE)
      );

      if (response.data.success) {
        setSuggestedProducts(response.data.data || []);
      }
    } catch (error) {
      console.error("Error fetching suggested products:", error);
    } finally {
      setLoadingSuggested(false);
    }
  };

  /* =============================
     APPLY suggested products AFTER
     products dropdown loads
  ============================== */
  useEffect(() => {
    if (products.length === 0 || suggestedProducts.length === 0) return;

    setItems((prevItems) => {
      const updated = [...prevItems];
      const max = Math.min(updated.length, suggestedProducts.length);

      for (let i = 0; i < max; i++) {
        const sp = suggestedProducts[i];
        const match = products.find(
          (p) => p.product_id === sp.product_id
        );

        if (match) {
          updated[i] = {
            ...updated[i],
            product_id: match.product_id,
            description: match.product_name,
            unit_price: Number(match.final_price),
            quantity: 1,
            amount: Number(match.final_price),
          };
        }
      }

      return updated;
    });
  }, [products, suggestedProducts]);

  /* =============================
     Fetch dropdown products
  ============================== */
  const fetchProducts = async () => {
    try {
      const response = await axios.get(
        "https://newerp.awasthivitality.com/api/products/for-invoice",
        { headers: getAuthHeaders() } // ✅ ADDED (SAFE)
      );
      if (response.data.success) {
        setProducts(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching products:", error);
    }
  };

  /* =============================
     Handle item change
  ============================== */
  const handleItemChange = (index, field, value) => {
    const updatedItems = [...items];

    if (field === "product_id") {
      const selectedProduct = products.find(
        (p) => p.product_id == value
      );

      if (selectedProduct) {
        updatedItems[index] = {
          ...updatedItems[index],
          product_id: selectedProduct.product_id,
          description: selectedProduct.product_name,
          unit_price: Number(selectedProduct.final_price),
          quantity: updatedItems[index].quantity || 1,
          amount:
            Number(selectedProduct.final_price) *
            (updatedItems[index].quantity || 1),
        };
      } else {
        updatedItems[index] = {
          ...updatedItems[index],
          product_id: null,
          description: "",
          unit_price: "",
          amount: "",
        };
      }
    } else if (field === "quantity") {
      const qty = Number(value) || 0;
      updatedItems[index].quantity = qty;
      updatedItems[index].amount =
        (Number(updatedItems[index].unit_price) || 0) * qty;
    } else if (field === "amount") {
      updatedItems[index].unit_price = Number(value) || "";
      updatedItems[index].amount =
        (Number(value) || 0) * (updatedItems[index].quantity || 1);
    }

    setItems(updatedItems);
  };

  /* =============================
     Totals
  ============================== */
  const totalAmount = items.reduce(
    (sum, item) => sum + (Number(item.amount) || 0),
    0
  );

  const totalAfterDiscount =
    totalAmount - (Number(discount) || 0);

  /* =============================
     Submit invoice (FIXED HEADERS)
  ============================== */
  const handleSubmit = async () => {
    if (!patient) return alert("Patient data not loaded!");
    if (!comment.trim()) return alert("Please enter Tnx!");

    const validItems = items.filter(
      (i) => i.product_id && i.quantity > 0 && i.amount > 0
    );

    if (!validItems.length) return alert("Add at least one product!");

    const invoiceData = {
      patient_id: patient.patient_id,
      invoice_date: new Date().toISOString().slice(0, 10),
      comment,
      remark: comment,
      discount_amount: discount,
      products: validItems.map((i) => ({
        product_id: i.product_id,
        description: i.description,
        quantity: i.quantity,
        unit_price: i.unit_price,
        amount: i.amount, // ✅ KEEP AS IS
      })),
    };

    try {
      setSubmitting(true);

      const res = await fetch(
        "https://newerp.awasthivitality.com/api/invoices/generate",
        {
          method: "POST",
          headers: getAuthHeaders(), // ✅ FIXED
          body: JSON.stringify(invoiceData),
        }
      );

      const result = await res.json();
      console.log("Invoice API Response:", result);

      if (result.success) {
        alert("Invoice generated successfully");
        navigate(`/patient/details/${patient_id}`);
      } else {
        alert(result.message || "Invoice failed");
      }
    } catch (err) {
      console.error(err);
      alert("Invoice failed");
    } finally {
      setSubmitting(false);
    }
  };

   const styles = {
    container: {
      maxWidth: "1200px",
      margin: "0 auto",
      padding: "20px",
      fontFamily: "Arial, sans-serif",
      backgroundColor: "#f8f9fa",
      minHeight: "100vh",
      marginLeft: `${leftMargin}px`,
      transition: "margin-left 0.3s ease",
    },
    header: { textAlign: "center", color: "#333", marginBottom: "30px", fontSize: "28px", fontWeight: "bold" },
    loadingMessage: { 
      textAlign: "center", 
      color: "#007bff", 
      marginBottom: "15px", 
      fontSize: "14px",
      fontStyle: "italic"
    },
    table: { width: "100%", borderCollapse: "collapse", marginBottom: "30px", background: "white", borderRadius: "12px", overflow: "hidden", boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)" },
    tableHeader: { backgroundColor: "rgb(47 178 231)", padding: "15px", textAlign: "left", fontWeight: "bold", color: "white" },
    tableCell: { padding: "12px 15px", borderBottom: "1px solid #dee2e6" },
    productSelect: { width: "100%", padding: "8px", border: "1px solid #007bff", borderRadius: "6px", fontSize: "15px", backgroundColor: "#f8f9ff" },
    numberInput: { width: "100%", padding: "8px", border: "1px solid #ddd", borderRadius: "6px", textAlign: "right" },
    totalAmount: { fontWeight: "bold", backgroundColor: "#f8f9fa", fontSize: "16px" },
    submitButton: { width: "100%", padding: "15px", backgroundColor: "#28a745", color: "white", marginBottom: "25px", border: "none", borderRadius: "8px", fontSize: "18px", fontWeight: "bold", cursor: "pointer" },
    submitButtonDisabled: { backgroundColor: "#6c757d", cursor: "not-allowed" },
    itemTotal: { fontSize: "12px", color: "#666", marginTop: "2px" },
  };
  return (
    <div style={styles.container}>
      <h2 style={styles.header}>Add Invoice</h2>
      
      {loadingSuggested && (
        <div style={styles.loadingMessage}>
          Loading suggested products based on patient's diagnosis...
        </div>
      )}

      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.tableHeader}>S.No.</th>
            <th style={styles.tableHeader}>Product/Service</th>
            <th style={styles.tableHeader}>Quantity</th>
            <th style={styles.tableHeader}>Unit Price</th>
            <th style={styles.tableHeader}>Total</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item, index) => (
            <tr key={index}>
              <td style={styles.tableCell}>{index + 1}</td>
              <td style={styles.tableCell}>
                <select
                  value={item.product_id || ""}
                  onChange={(e) =>
                    handleItemChange(index, "product_id", e.target.value)
                  }
                  style={styles.productSelect}
                >
                  <option value="">-- Select Product --</option>
                  {products.map((product) => (
                    <option key={product.product_id} value={product.product_id}>
                      {product.product_name} - ₹{product.final_price}
                    </option>
                  ))}
                </select>
              </td>
              <td style={styles.tableCell}>
                <input
                  type="number"
                  min="1"
                  value={item.quantity}
                  onChange={(e) =>
                    handleItemChange(index, "quantity", e.target.value)
                  }
                  style={styles.numberInput}
                />
              </td>
              <td style={styles.tableCell}>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={item.unit_price}
                  onChange={(e) =>
                    handleItemChange(index, "amount", e.target.value)
                  }
                  style={styles.numberInput}
                  disabled={item.product_id}
                />
              </td>
              <td style={styles.tableCell}>
                <div style={styles.itemTotal}>₹{(Number(item.amount) || 0).toFixed(2)}</div>
              </td>
            </tr>
          ))}
        </tbody>

        <tfoot>
          {/* Subtotal Row */}
          <tr style={{ backgroundColor: "#f8f9fa" }}>
            <td colSpan="4" style={{ ...styles.tableCell, textAlign: "right", fontWeight: "bold" }}>
              Subtotal:
            </td>
            <td style={styles.tableCell}>
              <div style={{ fontWeight: "bold" }}>₹{totalAmount.toFixed(2)}</div>
            </td>
          </tr>
  <tr style={{ backgroundColor: "#f1f1f1" }}>
    <td colSpan="4" style={{ ...styles.tableCell, textAlign: "right", fontWeight: "bold" }}>
      Tnx <span style={{ color: "red" }}>*</span>:
    </td>
    <td style={styles.tableCell}>
      <input
        type="text"
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        placeholder="Type anything..."
        style={{ ...styles.numberInput, textAlign: "left" }}
      />
    </td>
  </tr>

          {/* Discount Row */}
          <tr style={{ backgroundColor: "#f8f9fa" }}>
            <td colSpan="4" style={{ ...styles.tableCell, textAlign: "right", fontWeight: "bold" }}>
              Discount (₹):
            </td>
            <td style={styles.tableCell}>
              <input
                type="number"
                min="0"
                step="0.01"
                value={discount}
                onChange={(e) => setDiscount(Number(e.target.value || 0))}
                style={styles.numberInput}
                placeholder=""
              />
            </td>
          </tr>

          {/* Grand Total Row */}
          <tr style={{ backgroundColor: "#e9ecef", fontWeight: "bold" }}>
            <td colSpan="4" style={{ ...styles.tableCell, textAlign: "right" }}>
              Total After Discount:
            </td>
            <td style={styles.tableCell}>
              <div style={{ fontSize: "18px", fontWeight: "bold" }}>
                ₹{Math.max(totalAfterDiscount, 0).toFixed(2)}
              </div>
            </td>
          </tr>
        </tfoot>
      </table>

      <button
        onClick={handleSubmit}
        disabled={submitting || !patient}
        style={{
          ...styles.submitButton,
          ...(submitting || !patient ? styles.submitButtonDisabled : {}),
        }}
      >
        {submitting ? "Generating Invoice..." : "Generate Invoice"}
      </button>
    </div>
  );
};

export default AddInvoice;